"use client";
import { EmptyQueryBoundary, EmptyQueryHits } from "./EmptyQuery";
import ProductListItem from "./ProductListItem";
import { useProductSearchQuery } from "./ProductSearchContextProvider";
import useAutocomplete from "./hooks/useAutocomplete";
import {
  Suggestions,
  SearchResultWithHighlight,
  ProductSummaryWithHighlight,
} from "./types";
import styles from "./index.module.css";
import { sendAnalyticsProps } from "./index";
import { PRODUCT_INDEX_EN } from "@/constants/algolia";

interface ProductSearchSuggestionsProps {
  suggestions: Suggestions;
  sendAnalytics: (props: sendAnalyticsProps) => void;
  location?: string;
}

function ProductSearchSuggestions({
  suggestions,
  sendAnalytics,
  location,
}: ProductSearchSuggestionsProps) {
  const { query } = useProductSearchQuery();
  const results = useAutocomplete(query);
  const isProductIndex = PRODUCT_INDEX_EN === "product";

  const getUuid = (
    result: SearchResultWithHighlight | ProductSummaryWithHighlight,
  ) => {
    return isProductIndex
      ? (result as SearchResultWithHighlight).uuid
      : (result as ProductSummaryWithHighlight).productUuid;
  };

  return (
    <EmptyQueryBoundary
      fallback={
        <EmptyQueryHits suggestions={suggestions} location={location} />
      }
      query={query}
      results={results}
    >
      <div className="bg-white rounded shadow-md overflow-hidden">
        <ul className="list-none p-0 my-0">
          {results.map((result) => (
            <li
              key={getUuid(result)}
              className={`${styles.searchListItem} m-0 px-3 py-2`}
            >
              <ProductListItem hit={result} location={location} />
            </li>
          ))}
        </ul>
        <div className="pl-3 py-2 bg-light">
          <a
            onClick={() => {
              sendAnalytics({ fromViewAllButton: true });
            }}
            href={`/search${query?.trim().length ? `?q=${query.trim()}` : ""}`}
          >
            View all results
          </a>
        </div>
      </div>
    </EmptyQueryBoundary>
  );
}

export default ProductSearchSuggestions;
